// EDEN · the teacher notes of an exercise sheet, in teacher words (teacher review, 15 Oct 2026, point 8: the sheets
// read "like for computer to read"). Pure string work — no DOM, no curriculum code — so the document viewer stays
// bundled alone (scripts/doc-viewer-browser.mjs). Its cases are in scripts/test-sheet-words.mjs: real sentences from
// the sheets, unless marked "made up".
//
// What it does, on screen only (the sheet file is never changed; finding F-2026-10-15-01 asks for the real rewrite):
//   • a sentence that records how the sheet was BUILT ("…words[] stays empty", "…in the bundle manifest") is taken
//     off the note — cleanSheet() keeps it in the "for checking" fold as the sheet has it (only its week codes are
//     put in words there, as everywhere on the page);
//   • the builder's names are said the way a teacher reads the week: "the node" → "this week's plan", "the node's" →
//     "this week's", a word-set code → the set's name on the lesson page ("PET-01" → “Work & Jobs I”), "W12" →
//     "week 12", "E3" → "Exercise 3", a companion PDF's path → the name of its button on the lesson page.
// A code stays as written when it does not stand for exactly one set with words in its grade's word register: a set
// stored in parts (KEY-R04 is KEY-R04-a to KEY-R04-d), one of those parts, a planning marker with no words, or a set
// whose title says more than a name.

export interface NoteWords {
  /** The words for a Cambridge task-family code ("movers.speaking.p1"), or "" to keep the code. */
  taskWords?: (code: string) => string;
  /** The name of a word set on this sheet's lesson page ("PET-01" → "Work & Jobs I"), or undefined to keep the code. */
  setTitle?: (code: string) => string | undefined;
  /** The lesson page's button name for a companion PDF ("G10-T1-11-Speaking-Photos.pdf"), when it has one. */
  assetTitle?: (file: string) => string | undefined;
}

// ── sentences ──────────────────────────────────────────────────────────────────────────────────────────────────
/** Sentences of one text, each with its own trailing spaces, so they join back exactly. A sentence ends at . ! or ?
 *  (and a closing quote or bracket) before a space or the end — never at the dot inside "movers.speaking.p1",
 *  "Photos.pdf" or "1.5". No look-behind: older iOS Safari cannot parse one. */
export function sentencesOf(text: string): string[] {
  const out: string[] = [];
  const end = /[.!?]+["'’”)\]]*(?=\s|$)\s*/g;
  let start = 0;
  for (let m = end.exec(text); m; m = end.exec(text)) {
    out.push(text.slice(start, m.index + m[0].length));
    start = m.index + m[0].length;
  }
  if (start < text.length) out.push(text.slice(start));
  return out.length ? out : [text];
}

/** Words about the sheet's DATA or its build. A sentence that uses one folds even when it opens with an instruction
 *  ("Keep every words array empty because…"). Measured on all 217 sheets (15 Oct 2026): 31 sentences in 21 sheets;
 *  the two that also carry a classroom fact (G1 week 15's "no new spread … no paper", G7 week 3's "no word bank")
 *  say nothing the rest of their note does not. Each word here is the only one in at least one sentence it folds. */
const BUILD_DATA = /words\[\]|\bwords (?:array|field)\b|\bwords list is empty\b|\bmanifest\b|practice[- ]graph|derive_sb_offset|\bzero-link\b|\bmalformed inherited\b/i;
/** The builder's reasoning, which a sentence can share with a classroom instruction: such a sentence stays when it
 *  opens with one (TEACH_FIRST — the verbs a teacher's instruction opens with; one real sentence needs it today, Grade 1
 *  Term 2 week 10's "Teach 1-5 as the live node directs and do not expand the register's single 1-20 range token into
 *  an official-list claim"). */
const BUILD_TALK = /resolves to live week|official-list claim|promotion evidence|range token|structured [a-z-]+ event/i;
const TEACH_FIRST = /^\s*(?:\(\d+\)\s*)?(?:Teach|Use|Model|Keep|Do not|Don't|Give|Play|Read|Show|Say|Ask|Let|Start|Begin|Check|Collect|Photocopy|Print|Mark|Accept)\b/;

/** True when a whole sentence is the builder's record, to be folded away from the teacher's note. Never one that
 *  starts in lower case (the tail of a sentence cut at "…?"), and never one with a double quotation mark in it (a
 *  script or a model answer a teacher reads aloud). */
export function isBuildNote(sentence: string): boolean {
  if (/^\s*[a-z]/.test(sentence) || /["“”]/.test(sentence)) return false;
  return BUILD_DATA.test(sentence) || (BUILD_TALK.test(sentence) && !TEACH_FIRST.test(sentence));
}

// ── the builder's words ────────────────────────────────────────────────────────────────────────────────────────
/** "node" is the builder's word for one week's entry in the year plan — never the whole year plan: "The node has no
 *  pin" says this week's entry has none. Longest phrases first; case is kept. */
const NODE_WORDS: [RegExp, string][] = [
  [/\bnode (?=G\d{1,2}-T\d-\d{2}\b)/g, ""], // "(node G7-T1-10, …)" — the node id itself becomes "week 10" later
  [/\b([Tt])he (?:curriculum )?node for this week\b/g, "$1his week's plan"],
  [/\b([Tt])his week's node\b/g, "$1his week's plan"],
  [/\b([Tt])he live node\b/g, "$1his week's plan"],
  [/\btaught by this node\b/g, "taught this week"],
  [/\b([Tt])he node (title|objective|note)\b/g, "$1he $2 of this week's plan"], // "The node title in the syllabus"
  [/\b([Tt])he node(?=['’]s\b)/g, "$1his week"], // "the node's objective" → "this week's objective"
  [/\b([Tt])he node\b/g, "$1his week's plan"], // "The node has no coursebook entry" → "This week's plan has no …"
  [/\bTHE NODE\b/g, "THIS WEEK'S PLAN"],
  [/\bnode id\b/g, "week"],
  [/\bnodes\b/g, "weeks"],
  [/\bnode\b/g, "week"], // "this node's own summary" → "this week's own summary", "this guide-free node" → "… week"
  // the builder's other names: the exercise file's notes_to_teacher and has_image, the year plan's vocab_set and
  // task_family, and "bundle" for the sheet
  [/\bnotes_to_teacher\b/g, "the “Before you teach this” note"],
  [/\bvocab_set\b/g, "word set"],
  [/\btask_family\b/g, "exam task"],
  [/\s*\(has_image [^)]*\)/g, ""], [/,?\s*has_image stays false/g, ""],
  [/\b([Tt])his bundle\b/g, "$1his sheet"],
];

/** A Cambridge task-family code ("movers.speaking.p1", "key.rw.p1"); the app passes its own words for it. */
const TASK_CODE = /\b(?:starters|movers|flyers|ket|key|pet)\.(?:listening|rw|speaking|reading|writing|mock|intro|weak-parts|exam-craft|orientation|vocab)(?:\.[a-z0-9]+)*\b/gi;
/** A companion PDF by its builder path or file name: "assets-v1/G10-T1-11-Speaking-Photos.pdf",
 *  "`G8-T1-12-Listening-Visuals.pdf`". */
const ASSET_PDF = /`?(?:\bassets-v\d+\/)?\b(G\d{1,2}-T\d-\d{2}-[A-Za-z][A-Za-z-]*\.pdf)\b`?/g;

// ── word sets ──────────────────────────────────────────────────────────────────────────────────────────────────
const SET_ID = String.raw`(?:STA|MOV|FLY|KEY|PET|VIU)-[A-Za-z0-9]+(?:-[a-z][a-z0-9]*)*|S\d{2}-[a-z][a-z0-9]*(?:-[a-z0-9]+)*`;
/** A word-set code in running text: "PET-01", "STA-00-names", "FLY-R04", "S01-names", "STA-Transport". Not one inside
 *  a longer code or a range ("PET-01-07" and "FLY-R08-R" stay as written). */
const SET_CODE = new RegExp(String.raw`(^|[^\w/-])(${SET_ID})(?![\w-])`, "g");
/** A set code in brackets goes: "the school's Movers Names thread (MOV-01-names)", "(RET-3, wordless by design)", and
 *  in a list of words ("glad and guilty (PET-06), recipe (PET-04)") the words stay without their sets' codes. */
const CODE_LABEL = new RegExp(String.raw`\s*\((?:${SET_ID}|RET(?:RIEVAL)?-[\w-]+)(?:,\s*word-?less by design)?\)`, "g");
const MARKER = /\b(retrieval|review) (marker) (?:RET|RETRIEVAL)-[\w-]+/gi; // "the retrieval marker RET-1" → "the retrieval marker", in the note's own capitals
const NOUN = /^\s+(set|sets|word|words|list|thread|sequence)\b/;

/** "Places & Directions (pt 2)" → "place", "direction", "pt", "2": the words a title is made of, singular or plural
 *  ("the FLY-R13 animal set" is the “Animals” set, "a hobby set" the “Hobbies & Leisure” set). */
function stems(s: string): Set<string> {
  return new Set(s.toLowerCase().split(/[^a-z0-9]+/).filter((w) => w && w !== "and" && w !== "the").map((w) => w.replace(/ies$/, "y").replace(/s$/, "")));
}

/** Where the set's own name ends in `text` from `at`, or -1 when the words there are not its name. The name is either
 *  the title itself, its punctuation included ("Health, Medicine & Exercise", "Numbers (pt 2: 11-20, How many?)"), with
 *  "&", "/" and "and" standing for one another ("Family / Friends (pt 1)" for "Family & Friends (pt 1)"), or
 *  the title's words in the title's order with only spaces, "/", "&" or "and" between them ("TIME AND MEASUREMENTS"
 *  for "Time & measurements", "Feelings/Opinions adjectives" for "Feelings & Opinions adjectives"), in any case. The
 *  words may leave out one word from the middle of the title ("FEELINGS AND OPINIONS I" for "Feelings/Opinions
 *  adjectives I"), never the first or the last, so "WORK & JOBS" is not the name of "Work & Jobs I". The name ends
 *  with the title's last word (or the title's own last marks), and what follows it stays: ": time, daily, …", and
 *  ", medicine, doctor, …" after "HEALTH & EXERCISE" (any other mark ends the words: a comma, colon, bracket, dash).
 *  Either may start with the "HS ·" tag the lesson page shows for a head-start set ("HS · Money"): it is read as part
 *  of the name, which is said without it. */
function sameName(text: string, at: number, title: string): number {
  at += /^HS\s*·\s*/i.exec(text.slice(at))?.[0].length ?? 0;
  // the title itself: its words and marks in order, "&", "/" and "and" alike, spaces anywhere
  const piece = /[A-Za-z0-9]+|[^\sA-Za-z0-9]/g;
  const same = (s: string): string => (s === "&" || s === "/" ? "and" : s.toLowerCase());
  const want = (title.match(piece) ?? []).map(same);
  piece.lastIndex = at;
  let n = 0;
  let end = -1;
  for (let m = piece.exec(text); m && n < want.length && same(m[0]) === want[n]; m = piece.exec(text)) {
    n++;
    end = m.index + m[0].length;
  }
  if (n === want.length) return end;
  // the title's words in order
  const order = title.toLowerCase().split(/[^a-z0-9]+/).filter(Boolean); // "&" and the title's marks split it
  const word = /[A-Za-z0-9]+|&/g;
  word.lastIndex = at;
  let last = at; // the end of the last word read ("and" and "&" included)
  let k = 0; // how many of the title's words are read (or left out)
  let left = false; // whether a word from the middle was left out
  end = -1;
  for (let m = word.exec(text); m && k < order.length; m = word.exec(text)) {
    if (/[^\s/]/.test(text.slice(last, m.index))) break;
    const w = same(m[0]);
    last = m.index + m[0].length;
    if (w === order[k]) k++;
    else if (w === "and" && k > 0) continue; // only between the name's words: "STA-01 and colours" keeps its "and"
    else if (!left && k > 0 && k < order.length - 1 && w === order[k + 1]) { left = true; k += 2; }
    else break;
    end = last;
  }
  return k === order.length ? end : -1;
}

function setWords(text: string, titleOf: (code: string) => string | undefined): string {
  text = text.replace(CODE_LABEL, "").replace(MARKER, "$1 $2");
  const found: { start: number; end: number; title: string }[] = [];
  for (const m of text.matchAll(SET_CODE)) {
    const title = titleOf(m[2]!);
    if (title) found.push({ start: m.index! + m[1]!.length, end: m.index! + m[0].length, title });
  }
  let out = "";
  let pos = 0;
  for (const f of found) {
    let head = text.slice(pos, f.start);
    let tail = f.end;
    const after = text.slice(f.end);
    const q = `“${f.title}”`;
    let say = q;
    const own = /^(\s*\(\s*|,\s*|\s+)/.exec(after);
    const ownEnd = own ? sameName(after, own[0].length, f.title) : -1;
    const set = /^\s+set,\s*/.exec(after);
    const setEnd = set ? sameName(after, set[0].length, f.title) : -1;
    const noun = NOUN.exec(after);
    const topic = /^((?:\s+[a-z]+){1,4}?)\s+(set|words)\b/.exec(after);
    if (own && ownEnd > 0) {
      // "PET-01 (Work & Jobs I)", "PET-01, WORK & JOBS I", "KEY-04, TIME AND MEASUREMENTS", "PET-04 Food & Drink":
      // the code and then its own name. A bracket that goes on after the name ("(Work & Jobs I, week 2)") stays as written.
      if (!own[1]!.includes("(")) tail = f.end + ownEnd;
      else if (after[ownEnd] === ")") tail = f.end + ownEnd + 1;
      else say = text.slice(f.start, f.end);
    } else if (set && setEnd > 0) {
      // "the PET-02 set, Work & Jobs II" → the “Work & Jobs II” set
      say = `${q} set`;
      tail = f.end + setEnd;
    } else if (/\bthread set\s+$/.test(head)) {
      head = head.replace(/\bthread set\s+$/, "word set "); // "the school's Starters thread set STA-00-names"
    } else if (/\bthe school's set\s+$/.test(head)) {
      head = head.replace(/\bset\s+$/, "word set ");
    } else if (/\bregister:?\s+$/.test(head)) {
      head = head.replace(/\bregister:?\s+$/, "word set "); // "register STA-06-body (13 words…)"
    } else if (/^\s+register\b/.test(after)) {
      say = `${q} word set`; // "the wider STA-07 register"
      tail = f.end + /^\s+register\b/.exec(after)![0].length;
    } else if (noun) {
      // "the PET-01 words", "the S04-colours thread": the noun stays
    } else if (topic && [...stems(topic[1]!)].every((w) => stems(f.title).has(w))) {
      say = `${q} ${topic[2]}`; // "the FLY-R10 materials set" → the “Materials” set
      tail = f.end + topic[0].length;
    } else if (/\bsets?\s+(?:is|are)\s+$/i.test(head)) {
      // "the word set is PET-01" → is “Work & Jobs I”; but "THE WORD SET IS PET-01, <words that are not its name>"
      // stays as written: the sheet's own words follow, and a second name would contradict them
      if (/^\s*[,(]/.test(after)) say = text.slice(f.start, f.end);
    } else if (/\b(?:is|are)\s+$/.test(head)) {
      say = `from the ${q} word set`; // "the number is STA-02-numbers" → is from the “Numbers 1-20” word set
    } else if (/\b(?:from|in|of)\s+$/.test(head)) {
      say = `the ${q} word set`; // "from STA-03-toys (week 5)" → from the “Toys” word set (week 5)
    }
    out += head + say;
    pos = tail;
  }
  return out + text.slice(pos);
}

// ── weeks and exercises ────────────────────────────────────────────────────────────────────────────────────────
/** "W02-10" / "W1-W14" → weeks, "W12" → week 12, "E2-E4" → Exercises 2–4, "E1" → Exercise 1 — not inside a code
 *  ("FLY-W1") or a file name. A word that opens a sentence keeps its capital. */
function weekWords(text: string): string {
  const cap = (all: string, at: number, s: string): string => (/(?:^\s*|[.!?]\s+)$/.test(all.slice(0, at)) ? s.charAt(0).toUpperCase() + s.slice(1) : s);
  return text
    .replace(/(^|[^\w-])W(\d{1,2})-W?(\d{1,2})(?![\w-])/g, (_m, lead: string, a: string, b: string, at: number, all: string) => lead + cap(all, at + lead.length, `weeks ${Number(a)}–${Number(b)}`))
    // "the W2 queue" and "its own W1" are the weak-focus queues of a Grade 7 week, not weeks: they stay
    .replace(/(^|[^\w-])W(\d{1,2})(?![\w-])(?!\s+queue\b)/g, (m: string, lead: string, a: string, at: number, all: string) =>
      /\bown\s*$/.test(all.slice(0, at + lead.length)) ? m : lead + cap(all, at + lead.length, `week ${Number(a)}`))
    // "in Exercise E2" says "Exercise" already: only the number is added
    .replace(/(^|[^\w-])E(\d{1,2})(?:-E(\d{1,2}))?(?![\w-])/g, (_m, lead: string, a: string, b: string | undefined, at: number, all: string) =>
      lead + (/\bExercises?\s+$/i.test(all.slice(0, at + lead.length)) ? "" : b ? "Exercises " : "Exercise ") + (b ? `${Number(a)}–${Number(b)}` : `${Number(a)}`));
}

/** One kept piece of a teacher note, in teacher words. Node ids ("G6-T1-03") are left to the caller, which knows
 *  the sheet's own grade and term. */
export function noteWords(text: string, w: NoteWords = {}): string {
  let t = text.replace(ASSET_PDF, (m: string, file: string, at: number, all: string) => {
    const named = w.assetTitle?.(file)?.replace(/\s*\(PDF\)\s*$/i, "").trim();
    if (!named && !m.includes("assets-v")) return m; // a file name the app cannot name stays as written
    const the = /\b(?:the|a|an|this|its|compulsory|one-page)\s+$/i.test(all.slice(0, at)) ? "" : "the ";
    const bracket = /^\s*\(/.test(all.slice(at + m.length)); // "… .pdf (A4 actual size …)": no second bracket
    const where = bracket ? "from the lesson page" : "(on the lesson page)";
    return named ? `${the}“${named}” PDF ${where}` : `${the}PDF ${bracket ? "from the lesson page" : "on the lesson page"}`;
  });
  if (w.taskWords) t = t.replace(TASK_CODE, (code: string) => w.taskWords!(code.toLowerCase()) || code);
  for (const [rx, words] of NODE_WORDS) t = t.replace(rx, words);
  if (w.setTitle) t = setWords(t, w.setTitle);
  return weekWords(t);
}

/** A set's name for running text: the lesson page's title without its planning tags — "Sports & Leisure (pt 1) —
 *  recycling (a)" → "Sports & Leisure (pt 1)", "Toys (exposure)" → "Toys", "HS · Money (VIU 19)" → "Money" (HS: a
 *  B1 head-start set), "The Home" → "Home" ("the “Home” set"). "" when the title says more after its dash than a tag
 *  ("Review + Weather — remainder / Places: countryside"): a shortened name would misdescribe the words, so the code
 *  stays as written. */
export function inlineSetTitle(title: string): string {
  let t = title.replace(/\s*\([a-z]\)\s*$/, "");
  const dash = /\s+[—–]\s+(.*)$/.exec(t);
  if (dash) {
    if (!/^(?:recycling|new|new (?:key )?words|remainder)$/i.test(dash[1]!.trim())) return "";
    t = t.slice(0, dash.index);
  }
  return t.replace(/\s+-\s+PROPOSED$/i, "").replace(/\s*\((?!pt\b)[^)]*\)/g, "").replace(/^HS\s*·\s*/, "").replace(/^The\s+/, "").trim();
}

/** A sheet's word-set codes against its grade's register: the exact id, else the one set whose id starts with the
 *  code and a dash ("STA-05" → "STA-05-sports"; "S01-name" is not "S01-names"). Its code stays when the code matches several sets (a set stored in parts:
 *  "KEY-R04" is KEY-R04-a to -d), names one of those parts ("KEY-R01-a": the parts share a name), or names a set with
 *  no words (a planning marker) or a title that says more than a name (the map holds "" for both). */
export function setResolver(titles: ReadonlyMap<string, string> | null | undefined): (code: string) => string | undefined {
  if (!titles) return () => undefined;
  return (code) => {
    if (/-[a-z]$/.test(code)) return undefined; // one part of a set stored in parts ("KEY-R01-a")
    const exact = titles.get(code);
    if (exact !== undefined) return exact || undefined;
    const near = [...titles.keys()].filter((id) => id.startsWith(`${code}-`));
    return near.length === 1 ? titles.get(near[0]!) || undefined : undefined;
  };
}
